import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import {
  // eslint-disable-next-line no-restricted-imports
  useQuery as apolloUseQuery,
} from '@apollo/client/react';

import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useLogout as useLogOut } from '#src/hooks/auth/useLogOut.ts';
import { optimizeQuery } from '#src/lib/gql/optimizeQuery.ts';

let logoutTimeout: ReturnType<typeof setTimeout> | undefined;

// `returnPartialData` and `@defer` are unused, so `dataState` can only ever be
// 'complete' or 'empty'. Narrowing the union here keeps `data` as `TData | undefined`
// for callers instead of also admitting `DeepPartial<TData>`.
export type RefetchFunction<
  TData,
  TVariables extends OperationVariables,
> = apolloUseQuery.Result<TData, TVariables>['refetch'];

export type CompleteResult<
  TData,
  TVariables extends OperationVariables,
> = apolloUseQuery.Result<TData, TVariables, 'complete' | 'empty'>;

type AdditionalOptions = {
  logOutOnErrorCode?: number;
  skipCacheAndNetworkLoadingOverride?: boolean;
};

export const useQuery = <TData, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: apolloUseQuery.Options<NoInfer<TData>, NoInfer<TVariables>>,
  {
    logOutOnErrorCode,
    skipCacheAndNetworkLoadingOverride,
  }: AdditionalOptions = {
    logOutOnErrorCode: 422,
  }
): CompleteResult<TData, TVariables> => {
  const applogOut = useLogOut();

  const q = optimizeQuery.current(query);

  // `Options` embeds VariablesOption<TVariables>, whose required-ness is
  // conditional on TVariables. Spreading an optional `options` can't satisfy that
  // for an unresolved generic, so assert once here rather than at every use.
  const apolloOptions = {
    fetchPolicy: 'cache-and-network',
    ...options,
  } as apolloUseQuery.Options<NoInfer<TData>, NoInfer<TVariables>>;

  // Work-around the pollInterval to keep firing even when skip is set
  // https://github.com/apollographql/react-apollo/issues/2127#issuecomment-607844722
  if (apolloOptions.skip && apolloOptions.pollInterval) {
    apolloOptions.pollInterval = 0;
  }

  const res = apolloUseQuery(q, apolloOptions) as CompleteResult<
    TData,
    TVariables
  >;

  // When using `cache-and-network` policy, `loading` will be `true`
  // even in case of cache hit.
  // This ensure that should `loading` be set, nothing is in the cache.
  // Hack inspired from https://github.com/apollographql/apollo-client/issues/8669
  if (
    apolloOptions.fetchPolicy === 'cache-and-network' &&
    res.loading &&
    res.data &&
    !skipCacheAndNetworkLoadingOverride
  ) {
    // oxlint-disable-next-line react/immutability
    res.loading = false;
  }

  const walletCtx = useWalletContext();

  const logOut = () => {
    (async () => {
      try {
        if (walletCtx) {
          await walletCtx.logOut();
        } else {
          await applogOut();
        }
      } finally {
        logoutTimeout = undefined;
      }
    })();
  };

  // v4 surfaces GraphQL errors as CombinedGraphQLErrors and everything else as
  // the transport error itself, so anything that isn't the former is what v3
  // called `networkError`.
  if (res.error && !CombinedGraphQLErrors.is(res.error)) {
    if (
      ServerError.is(res.error) &&
      res.error.statusCode === logOutOnErrorCode
    ) {
      // Allow only one logout at a time to avoid infinite loop
      if (!logoutTimeout) {
        // logout asynchronously to avoid running into "Store reset while query was in flight" error
        // oxlint-disable-next-line react/globals
        logoutTimeout = setTimeout(logOut, 1000);
      }

      return res;
    }
    throw res.error;
  }

  return res;
};
